/**
 * A geocode bias anchor derived from the caller's phone area code.
 *
 * ## Why this exists
 *
 * The anchor chain (see `anchor.ts`) is GPS-first, and GPS is frequently absent: the
 * citizen declines the consent prompt, the officer is not sharing, or the call arrives
 * over PSTN where there is no browser to ask. With no anchor we send no proximity, and
 * an ambiguous address can rank a match on another continent first — measured live,
 * `145 Industrial Park Drive, Etobicoke` resolved to Grayville, ILLINOIS, 918 km away.
 *
 * A phone number is almost always AVAILABLE on a call. That is not the same as it
 * being a location signal: it is a claim about who someone is reachable at, which
 * a ported, nomadic, relayed, borrowed or misattributed number severs from where
 * they are. It anchors only when the call has established nothing better — see the
 * precedence note in `anchor.ts`.
 *
 * ## Why NOT the province, which is the obvious thing to reach for
 *
 * `addressContext.ts` already maps area codes to PROVINCES for address completion.
 * Reusing that here would be actively harmful: Ontario's geographic centroid is around
 * 50°N in the northern bush, roughly 500 km from Toronto, where the overwhelming
 * majority of calls originate. A province centroid would bias most Ontario calls AWAY
 * from the caller — worse than no anchor, because it is confidently wrong.
 *
 * Area codes are SERVING-REGION scoped, which is the granularity that helps: 416 is
 * Toronto, 613 is Ottawa, 604 is Greater Vancouver. So this table maps each code to the
 * centre of the region it actually serves.
 *
 * ## What this is and is not
 *
 * ⛔ This is a BIAS, never a filter. It changes which candidate ranks first; it must
 *    never cause a result to be discarded. A caller reporting an incident 300 km from
 *    their phone's area code must still geocode — that lesson already cost a revert.
 *
 * These anchors are COARSE by construction — tens to low hundreds of km. That is
 * acceptable precisely because the job is to pick the right metropolitan region, not
 * the right street. Getting "somewhere near Toronto" instead of "somewhere in Illinois"
 * is the entire value.
 *
 * ## Omissions are deliberate
 *
 * Codes whose serving region is too large for a centroid to mean anything are LEFT OUT
 * rather than guessed at. An anchor 600 km from the caller is not a weak signal, it is
 * a wrong one, and this chain already prefers "no anchor" to a bad anchor.
 */

export interface AreaCodeAnchor {
  lat: number;
  lon: number;
  /** Rough radius of the serving region, metres. Honest, and deliberately large. */
  accuracyM: number;
  /** Human-readable region, for logging and for explaining a pin. */
  region: string;
}

/**
 * Area code -> centre of the region it serves.
 *
 * Coordinates are the principal population centre of each code's territory, not the
 * geographic centroid, because callers are where the people are. `accuracyM` reflects
 * how far a caller might plausibly be from that point within the same code.
 */
const AREA_CODE_ANCHORS: Record<string, AreaCodeAnchor> = {
  // --- Ontario -------------------------------------------------------------
  // Toronto proper (416/647/437) and its 683 overlay.
  '416': { lat: 43.6532, lon: -79.3832, accuracyM: 25_000, region: 'Toronto' },
  '647': { lat: 43.6532, lon: -79.3832, accuracyM: 25_000, region: 'Toronto' },
  '437': { lat: 43.6532, lon: -79.3832, accuracyM: 25_000, region: 'Toronto' },
  '683': { lat: 43.6532, lon: -79.3832, accuracyM: 25_000, region: 'Toronto' },
  // The 905 ring around Toronto — Mississauga/Brampton/Hamilton/Durham. Wider.
  '905': { lat: 43.7000, lon: -79.5500, accuracyM: 60_000, region: 'Greater Toronto Area' },
  '289': { lat: 43.7000, lon: -79.5500, accuracyM: 60_000, region: 'Greater Toronto Area' },
  '365': { lat: 43.7000, lon: -79.5500, accuracyM: 60_000, region: 'Greater Toronto Area' },
  '942': { lat: 43.7000, lon: -79.5500, accuracyM: 60_000, region: 'Greater Toronto Area' },
  // Ottawa and its overlays.
  '613': { lat: 45.4215, lon: -75.6972, accuracyM: 50_000, region: 'Ottawa' },
  '343': { lat: 45.4215, lon: -75.6972, accuracyM: 50_000, region: 'Ottawa' },
  '753': { lat: 45.4215, lon: -75.6972, accuracyM: 50_000, region: 'Ottawa' },
  // Southwestern Ontario (519/226/548/382). One code, several cities: Windsor,
  // London, Kitchener-Waterloo-Cambridge, Guelph, Brantford, Sarnia, Owen Sound.
  // The anchor sits on the WOODSTOCK-INGERSOLL corridor rather than on London,
  // because the Waterloo Region + Guelph cluster (~700k) outweighs London (~420k)
  // and pulling to London alone under-weights where most of these callers are.
  // Windsor is the far end at ~200 km, which the accuracy states honestly.
  '519': { lat: 43.2000, lon: -80.6000, accuracyM: 150_000, region: 'Southwestern Ontario' },
  '226': { lat: 43.2000, lon: -80.6000, accuracyM: 150_000, region: 'Southwestern Ontario' },
  '548': { lat: 43.2000, lon: -80.6000, accuracyM: 150_000, region: 'Southwestern Ontario' },
  '382': { lat: 43.2000, lon: -80.6000, accuracyM: 150_000, region: 'Southwestern Ontario' },
  // Central/northeastern Ontario (705/249/742). Barrie, Orillia and Peterborough in
  // the south (~280k) roughly balance Sudbury, North Bay and Sault Ste. Marie in the
  // north (~290k), so the anchor sits between them. Sault Ste. Marie is the far end at
  // ~400 km — stated in the accuracy rather than hidden.
  //
  // ⚠ 742 was MISSING from this table on first write while 705/249 were present. It is
  // the 2023 overlay for exactly this region, so a 742 caller silently got no anchor at
  // all. Overlay codes are the easy ones to miss precisely because they carry no new
  // geography — cross-check against the Ontario block of CANADIAN_AREA_CODES, which is
  // what surfaced it.
  '705': { lat: 45.3000, lon: -79.7000, accuracyM: 200_000, region: 'Central Ontario' },
  '249': { lat: 45.3000, lon: -79.7000, accuracyM: 200_000, region: 'Central Ontario' },
  '742': { lat: 45.3000, lon: -79.7000, accuracyM: 200_000, region: 'Central Ontario' },
  // Northwestern Ontario (807). Thunder Bay holds roughly half the district's
  // population, so it is the honest anchor, but the code also covers Kenora ~490 km
  // west and Fort Severn far north.
  //
  // ⚠ TRADE-OFF, stated because it is real: for a Kenora-area caller this anchor is
  // ~490 km off, and the distance-aware gate will refuse a MODERATE-score match near
  // them as "far from anchor". A high-confidence match is still accepted at ANY
  // distance, so the harm is bounded to marginal matches. Included anyway because the
  // alternative is no anchor for all of northwestern Ontario, and the Thunder Bay
  // majority gains a correct one. Revisit if NW Ontario call volume justifies it.
  '807': { lat: 48.3809, lon: -89.2477, accuracyM: 300_000, region: 'Northwestern Ontario' },

  // --- Quebec --------------------------------------------------------------
  '514': { lat: 45.5019, lon: -73.5674, accuracyM: 25_000, region: 'Montreal' },
  '438': { lat: 45.5019, lon: -73.5674, accuracyM: 25_000, region: 'Montreal' },
  '450': { lat: 45.5500, lon: -73.7500, accuracyM: 70_000, region: 'Greater Montreal' },
  '579': { lat: 45.5500, lon: -73.7500, accuracyM: 70_000, region: 'Greater Montreal' },
  '354': { lat: 45.5500, lon: -73.7500, accuracyM: 70_000, region: 'Greater Montreal' },
  '418': { lat: 46.8139, lon: -71.2080, accuracyM: 120_000, region: 'Quebec City region' },
  '581': { lat: 46.8139, lon: -71.2080, accuracyM: 120_000, region: 'Quebec City region' },
  '367': { lat: 46.8139, lon: -71.2080, accuracyM: 120_000, region: 'Quebec City region' },
  '468': { lat: 46.8139, lon: -71.2080, accuracyM: 120_000, region: 'Quebec City region' },

  // --- British Columbia ----------------------------------------------------
  '604': { lat: 49.2827, lon: -123.1207, accuracyM: 40_000, region: 'Greater Vancouver' },
  '778': { lat: 49.2827, lon: -123.1207, accuracyM: 40_000, region: 'Greater Vancouver' },
  '236': { lat: 49.2827, lon: -123.1207, accuracyM: 40_000, region: 'Greater Vancouver' },
  '672': { lat: 49.2827, lon: -123.1207, accuracyM: 40_000, region: 'Greater Vancouver' },
  '257': { lat: 49.2827, lon: -123.1207, accuracyM: 40_000, region: 'Greater Vancouver' },

  // --- Alberta -------------------------------------------------------------
  '403': { lat: 51.0447, lon: -114.0719, accuracyM: 90_000, region: 'Calgary and southern Alberta' },
  '780': { lat: 53.5461, lon: -113.4938, accuracyM: 90_000, region: 'Edmonton and northern Alberta' },
  // 587/825/368 overlay the WHOLE province, straddling Calgary and Edmonton (~280 km
  // apart). The midpoint sits ~140 km from each — inside the near-anchor radius for
  // both, so it is still a useful bias, but the accuracy says so honestly.
  '587': { lat: 52.2800, lon: -113.8100, accuracyM: 160_000, region: 'Alberta' },
  '825': { lat: 52.2800, lon: -113.8100, accuracyM: 160_000, region: 'Alberta' },
  '368': { lat: 52.2800, lon: -113.8100, accuracyM: 160_000, region: 'Alberta' },

  // --- Prairie and Atlantic, where population is concentrated in one centre --
  '204': { lat: 49.8951, lon: -97.1384, accuracyM: 90_000, region: 'Winnipeg region' },
  '431': { lat: 49.8951, lon: -97.1384, accuracyM: 90_000, region: 'Winnipeg region' },
  '584': { lat: 49.8951, lon: -97.1384, accuracyM: 90_000, region: 'Winnipeg region' },
  // Saskatoon and Regina are ~240 km apart; the midpoint keeps both inside the radius.
  '306': { lat: 51.3000, lon: -105.6000, accuracyM: 140_000, region: 'Saskatchewan' },
  '639': { lat: 51.3000, lon: -105.6000, accuracyM: 140_000, region: 'Saskatchewan' },
  '474': { lat: 51.3000, lon: -105.6000, accuracyM: 140_000, region: 'Saskatchewan' },
  '506': { lat: 46.0000, lon: -66.0000, accuracyM: 150_000, region: 'New Brunswick' },
  '428': { lat: 46.0000, lon: -66.0000, accuracyM: 150_000, region: 'New Brunswick' },
  // 902/782 cover Nova Scotia AND Prince Edward Island. Halifax dominates population
  // and Charlottetown is ~200 km away, still inside the radius.
  '902': { lat: 44.6488, lon: -63.5752, accuracyM: 150_000, region: 'Nova Scotia and PEI' },
  '782': { lat: 44.6488, lon: -63.5752, accuracyM: 150_000, region: 'Nova Scotia and PEI' },
  // Newfoundland: St John's holds most of the population. Labrador is enormous, which
  // the accuracy reflects rather than hides.
  '709': { lat: 47.5615, lon: -52.7126, accuracyM: 200_000, region: 'Newfoundland' },
  '879': { lat: 47.5615, lon: -52.7126, accuracyM: 200_000, region: 'Newfoundland' },
};

/**
 * DELIBERATELY OMITTED — do not "complete" this table without measuring first.
 *
 *   867  Yukon + Northwest Territories + Nunavut. Whitehorse to Iqaluit is ~2,900 km.
 *        No single point is a useful bias, and a wrong one is worse than none.
 *   250  British Columbia outside Metro Vancouver — Vancouver Island, the Interior and
 *        the north. Victoria to Prince George is ~700 km.
 *   819  Western/central Quebec. Gatineau to Val-d'Or is ~400 km.
 *   873  Overlay of 819, same problem.
 *
 * Each of these would place a meaningful share of callers hundreds of km from the
 * anchor. Returning nothing keeps the "no anchor" path, which is honest.
 */
export const OMITTED_AREA_CODES = ['867', '250', '819', '873'] as const;

/**
 * Resolve a bias anchor from a NANP area code. Returns null when the code is unknown,
 * non-Canadian, or deliberately omitted for being too broad to mean anything.
 */
export function anchorForAreaCode(areaCode: string | null | undefined): AreaCodeAnchor | null {
  if (!areaCode) return null;
  return AREA_CODE_ANCHORS[areaCode] ?? null;
}

/** Exposed for tests and diagnostics — how many codes carry an anchor. */
export function areaCodeAnchorCount(): number {
  return Object.keys(AREA_CODE_ANCHORS).length;
}
